import QuestionTooltip from '@uikit/QuestionTooltip/QuestionTooltip';
import Loader from '@uikit/old/Loader/Loader';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
// eslint-disable-next-line import/no-extraneous-dependencies
import cn from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import qs from 'query-string';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';

import {
  AppSumoTokensResponseType,
  LoginResponseType,
  VerificationEmailResponseType,
} from '@ts/auth.types';

import {
  AuthHandlerType,
  AuthHandlerTypeMap,
  VerificationCodeType,
} from 'respona_api/generated/auth-shared_pb';
import { BasePreloaderScreen } from 'src/BaseRequestComponent';
import { authService } from 'src/services/authService';

import {
  acceptInvitationApi,
  getAppSumoAccessTokenApi,
  getTimeZone,
  loginByIntegrationApi,
  showReCaptchaApi,
} from '@api/auth.api';

import { LoginPagePaymentFlow } from '@pages/LoginPage/_components/LoginPagePaymentFlow';

import {
  CodeConfirmationFlow,
  ConciergeOnboardingScheduleCall,
  ConciergeOnboardingTransitionFlow,
  LoginFlow,
  LoginSignupFlow,
  OnboardingSessionFlow,
  RequestUpdatePasswordFlow,
  SignUpFlow,
  UpdatePasswordFlow,
} from '@pages/LoginPage/_components/LoginPageFlows';

import './LoginPage.scss';

import { calendlyStatusTypes } from '@constants/onboardingPopupStatuses';
import { setOnboardingStep } from '@api/analytics.api';
// @ts-ignore
import { useGoogleLogin } from '@react-oauth/google';
// @ts-ignore
import gtm from 'src/libs/gtm';
import { skipOnboardingSessionApi } from '@api/user.api';
import { redirectUserTo } from '@utils/historyHandler';
import { CodeType } from 'respona_api/generated/common_pb';
import { getRealm, isAppRealm, isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';
import { getItemFromStorage, setItemInStorage } from '@utils/localStorageHandler';
import { isSemrushMode, isStandaloneMode } from '@constants/app-modes';
// @ts-ignore
import { SVGIcon } from '@uikit/old/Icon/Icon';
import { addNotification } from '@redux/actions/notifications.actions';
import { useDispatch } from 'react-redux';
import { DispatchType } from 'src/store';
// @ts-ignore
import LoginImageBig from './assets/loginImage.svg';
// @ts-ignore
import ConciergeLoginImageBig from './assets/conciergeLoginImage.svg';
// @ts-ignore
import PublisherLoginImageBig from './assets/publisherLoginImage.svg';
// @ts-ignore
import responaLogo from '../../assets/ResponaLogoLarge.svg';

declare const IS_DEV: boolean;
declare const REALM: string;

export enum LoginStep {
  welcome = 'welcome',
  login = 'login',
  signUp = 'register',
  auth = 'authorize/login',
  confirm = 'confirm',
  onboardingApp = 'onboarding',
  onboardingConciergeQuestionary = 'onboarding/referral-source',
  onboardingConciergeFirstOrder = 'transition-first-order',
  onboardingConciergeScheduleCall = 'schedule-call',
  onboardingPublisher = 'onboarding/type-of-company',
  trial = 'trial',
  forgotPassword = 'forgot-password',
  restore = 'restore',
}

// const getContainerMaxHeight = (step: LoginStep) => {
//   switch (step) {
//     case LoginStep.welcome:
//       // return '500px';
//       return '625px';
//     case LoginStep.signUp:
//       return '664px';
//     case LoginStep.login:
//       return '535px';
//     case LoginStep.confirm:
//       return '411px';
//     case LoginStep.onboarding:
//       return '565px';
//     case LoginStep.trial:
//       return '655px';
//     case LoginStep.forgotPassword:
//       return '428px';
//     case LoginStep.restore:
//       return '540px';
//     default:
//       return 'unset';
//   }
// };

// Session cache for the AppSumo activation flow. The authorization code in the URL
// is one-time: re-exchanging it (e.g. after a page refresh mid-signup) always fails,
// which used to silently degrade the buyer to a TRIAL signup with no linked license.
const APPSUMO_SESSION_KEY = 'appsumoAuth';

function LoginPage({ history, location }: any): JSX.Element {
  const parsedSearch = qs.parse(location.search);

  const dispatch = useDispatch<DispatchType>();
  const {
    token,
    ref,
    src,
    ps_xid,
    code: appSumoCodeParam,
    isRightSideVisible: queryIsRightSideVisible,
  } = parsedSearch;

  const [step, setStep] = useState<LoginStep>(LoginStep.welcome);
  const [isRightSideVisible, setIsRightSideVisible] = useState<boolean>(!!queryIsRightSideVisible);

  const [state, setState] = useState({
    email: '',
    password: '',
    fullName: '',
    code: '',
    captcha: '',
    newPassword: '',
    showCaptcha: true,
    type: AuthHandlerType.NONE,
  });

  const [emailVerification, setEmailVerification] = useState<VerificationEmailResponseType>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [appSumoCode, setAppSumoCode] = useState<string | null>(null);
  const [appSumoTokens, setAppSumoTokens] = useState<AppSumoTokensResponseType | null>(null);
  // AppSumo flow is active as soon as a code was seen (URL or session cache); it is
  // blocked while active without verified tokens. Signup must not proceed while
  // blocked, otherwise the buyer gets a TRIAL org and the paid license is dropped.
  const appSumoFlowActive = !!appSumoCode;
  const appSumoBlocked = appSumoFlowActive && !appSumoTokens;

  const guardAppSumoSignup = (): boolean => {
    if (appSumoBlocked) {
      dispatch(
        addNotification({
          type: 'error',
          title:
            "Your AppSumo code couldn't be verified, so signup can't continue. Please return to AppSumo and open your activation link again.",
        })
      );
      return false;
    }
    return true;
  };
  const [loginResponse, setLoginResponse] = useState<LoginResponseType>(null);
  const setupDemo = useRef<boolean>(false);

  const { data: showCaptchaStatus, isSuccess } = useQuery({
    queryKey: 'recaptcha',
    queryFn: () => showReCaptchaApi(),
    refetchOnWindowFocus: false,
    onSuccess: (data) => handleInput('showCaptcha', data.show),
  });

  useEffect(() => {
    if (queryIsRightSideVisible) {
      setIsRightSideVisible(true);
    } else {
      setIsRightSideVisible(false);
    }
  }, [queryIsRightSideVisible]);

  const handleInput = (
    key: string,
    value: string | boolean | AuthHandlerTypeMap[keyof AuthHandlerTypeMap]
  ) => {
    setState((data) => ({ ...data, [key]: value }));
  };

  const updateRef = (key: string, value: boolean) => {
    if (key === 'setupDemo') {
      setupDemo.current = value;
    }
  };

  // First-login concierge (DfY) onboarding entry point: business-email signups are
  // prompted to schedule a demo call first, everyone else goes straight to the
  // "How did you hear about Respona?" questionary.
  const conciergeFirstLoginStep = (response: LoginResponseType): LoginStep =>
    response?.businessEmail
      ? LoginStep.onboardingConciergeScheduleCall
      : LoginStep.onboardingConciergeFirstOrder;

  const loginWithGoogle = useGoogleLogin({
    onSuccess: ({ code }) => {
      if (!guardAppSumoSignup()) {
        return;
      }
      loginByIntegrationApi(
        code,
        [
          { key: referralCode, type: CodeType.REFERRAL_CODE },
          { key: affiliateCode, type: CodeType.PARTNER_STACK },
          ...(appSumoTokens
            ? [
                { key: appSumoTokens.accessToken, type: CodeType.APP_SUMO_AUTH_TOKEN },
                { key: appSumoTokens.refreshToken, type: CodeType.APP_SUMO_REFRESH_TOKEN },
              ]
            : []),
        ],
        src as string
      ).then((result) => {
        setLoginResponse(result);
        if (result.firstLogin) {
          sessionStorage.removeItem(APPSUMO_SESSION_KEY);
          if (result.successMessage?.length > 0) {
            dispatch(addNotification({ type: 'success', title: result.successMessage }));
          }
          // Persist token before onboarding redirect so protected onboarding APIs succeed
          if (isStandaloneMode() && result.sessionToken) {
            setItemInStorage('token', result.sessionToken);
          }
          gtm.signUp({ userId: result.userId, email: result.userEmail, method: 'google' });
          gtm.signIn({ userId: result.userId, email: result.userEmail, method: 'google' });
          if (isAppRealm()) {
            redirectUserTo(history, `/${LoginStep.onboardingApp}`);
          } else if (isConciergeRealm()) {
            redirectUserTo(history, `/${conciergeFirstLoginStep(result)}`);
          } else if (isPublisherRealm()) {
            redirectUserTo(history, `/${LoginStep.onboardingPublisher}`);
          }
        } else {
          authService.loginCallback(history, result);
        }
      });
    },
    onError: (errorResponse) => {
      console.error('Google Auth error', errorResponse);
    },
    flow: 'auth-code',
    scope: 'profile email',
  });

  const composeMicrosoft = () => {
    const refPart = ref ? `&ref=${ref}` : '';
    const srcPart = src ? `&src=${src}` : '';
    const pxdPart = ps_xid ? `&ps_xid=${ps_xid}` : '';
    const codePart = appSumoCodeParam ? `&app_code=${appSumoCodeParam}` : '';
    const parts = `${refPart}${srcPart}${pxdPart}${codePart}&realm=${REALM}`;
    let redirectUrl;
    if (isConciergeRealm()) {
      redirectUrl = IS_DEV ? 'dev-concierge.respona.com' : 'dashboard.respona.com';
    } else if (isPublisherRealm()) {
      redirectUrl = IS_DEV ? 'dev-publisher.respona.com' : 'publisher.respona.com';
    } else {
      redirectUrl = IS_DEV ? 'dev.respona.com' : 'app.respona.com';
    }

    return `https://${redirectUrl}/auth/microsoft/secure/aad?time-zone=${encodeURIComponent(getTimeZone())}${parts}`;
  };

  const completeStep = async (step: LoginStep, response?: LoginResponseType) => {
    switch (step) {
      case LoginStep.welcome: {
        setIsLoading(true);
        return authService
          .checkAccountByEmail(state.email)
          .then((res) => {
            setEmailVerification(res);
            if (res.exist) {
              // H7: an AppSumo license can only be bound during registration (the org
              // UID is the license key). Proceeding into an existing-account login
              // used to silently drop the paid license after the buyer had already
              // seen "AppSumo code verified".
              if (appSumoFlowActive) {
                dispatch(
                  addNotification({
                    type: 'error',
                    title:
                      "This AppSumo license can't be applied to an existing Respona account. Please sign up with a different email address, or contact support to link it to this account.",
                  })
                );
                return;
              }
              handleInput('type', res.type);
              if (res.type === AuthHandlerType.GOOGLE) {
                loginWithGoogle();
              } else if (res.type === AuthHandlerType.MICROSOFT) {
                window.open(composeMicrosoft(), '_self');
              } else {
                redirectUserTo(history, `/${LoginStep.login}`);
              }
            } else if (res.valid) {
              if (!guardAppSumoSignup()) {
                return;
              }
              authService
                .sendVerificationCode(state.email, VerificationCodeType.REGISTRATION)
                .then(() => {
                  redirectUserTo(history, `/${LoginStep.signUp}${history.location.search}`);
                })
                .catch((e) => console.error(e));
            }
          })
          .finally(() => setIsLoading(false));
      }

      case LoginStep.login: {
        setIsLoading(true);
        return authService
          .login(history, state.email, state.password, state.captcha)
          .finally(() => setIsLoading(false));
      }

      case LoginStep.signUp: {
        setIsLoading(true);

        if (token) {
          acceptInvitationApi(state.fullName, state.password, token as string, state.captcha)
            .then((loginResp: LoginResponseType) => {
              setLoginResponse(loginResp);
              if (loginResp.firstLogin) {
                gtm.signUp({
                  userId: loginResp.userId,
                  email: loginResp.userEmail,
                  method: 'invitation',
                });
              }
              gtm.signIn({
                userId: loginResp.userId,
                email: loginResp.userEmail,
                method: 'invitation',
              });
              authService.loginCallback(history, loginResp, true);
            })
            .catch((e) => {
              setIsLoading(false);
              return Promise.reject(e);
            });
          break;
        } else {
          redirectUserTo(history, `/${LoginStep.confirm}${history.location.search}`);

          setIsLoading(false);

          return Promise.resolve();
        }
      }

      case LoginStep.onboardingConciergeScheduleCall:
      case LoginStep.onboardingConciergeFirstOrder: {
        setIsLoading(true);
        const currentResponse = response || loginResponse;
        if (!currentResponse) {
          setIsLoading(false);
          return;
        }
        const { sessionToken } = currentResponse;
        await setItemInStorage('token', sessionToken);
        gtm.signIn({ userId: currentResponse.userId, email: currentResponse.userEmail });
        setIsLoading(false);
        redirectUserTo(history, `/${conciergeFirstLoginStep(currentResponse)}`);
        return Promise.resolve();
      }

      case LoginStep.onboardingApp:
      case LoginStep.onboardingConciergeQuestionary:
      case LoginStep.onboardingPublisher: {
        setIsLoading(true);

        const currentResponse = response || loginResponse;
        if (!currentResponse) {
          setIsLoading(false);
          return;
        }
        const { userEmail } = currentResponse;

        if (isAppRealm() && !isSemrushMode() && setupDemo.current) {
          const nameNor = encodeURIComponent(state.fullName);
          const emailNor = state?.email || userEmail;
          window.open(
            `https://respona.ewebinar.com/webinar/start-your-first-respona-campaign-in-30-minutes-19178/register?showform=1&Name=${nameNor}&email=${emailNor}&proprietaryId=${emailNor}`,
            '_blank',
            'noopener,noreferrer'
          );
          localStorage.setItem('showCalendly', calendlyStatusTypes.COMPLETE);
          await skipOnboardingSessionApi();
          await setOnboardingStep(1);
        }

        setIsLoading(false);

        if (isAppRealm()) {
          localStorage.setItem('firstCampaign', 'true');
        }
        gtm.signIn({ userId: currentResponse.userId, email: currentResponse.userEmail });
        authService.loginCallback(history, currentResponse);

        return Promise.resolve();
      }

      case LoginStep.confirm: {
        // Fail closed: registering without verified AppSumo tokens would create a
        // plain TRIAL org and permanently orphan the purchased license.
        if (!guardAppSumoSignup()) {
          return Promise.resolve();
        }
        setIsLoading(true);
        return authService
          .register(
            state.email,
            state.password,
            state.fullName,
            state.captcha,
            state.code,
            [
              { key: referralCode, type: CodeType.REFERRAL_CODE },
              { key: affiliateCode, type: CodeType.PARTNER_STACK },
              ...(appSumoTokens
                ? [
                    { key: appSumoTokens.accessToken, type: CodeType.APP_SUMO_AUTH_TOKEN },
                    { key: appSumoTokens.refreshToken, type: CodeType.APP_SUMO_REFRESH_TOKEN },
                  ]
                : []),
            ],
            src as string
          )
          .then(async (response: LoginResponseType) => {
            setLoginResponse(response);
            if (response.firstLogin) {
              sessionStorage.removeItem(APPSUMO_SESSION_KEY);
              if (response.successMessage?.length > 0) {
                dispatch(addNotification({ type: 'success', title: response.successMessage }));
              }
              gtm.signUp({
                userId: response.userId,
                email: response.userEmail,
                method: 'email',
              });
              gtm.signIn({
                userId: response.userId,
                email: response.userEmail,
                method: 'email',
              });
              const { sessionToken } = response;
              await setItemInStorage('token', sessionToken);
              if (isAppRealm()) {
                redirectUserTo(history, `/${LoginStep.onboardingApp}`);
              } else if (isConciergeRealm()) {
                redirectUserTo(history, `/${conciergeFirstLoginStep(response)}`);
              } else if (isPublisherRealm()) {
                redirectUserTo(history, `/${LoginStep.onboardingPublisher}`);
              }
            } else {
              authService.loginCallback(history, response);
            }
          })
          .catch((e) => {
            // Without an explicit catch, a rejected register() call surfaces only
            // as an "Uncaught (in promise)" in the console: backend errors with
            // `visible: false` (e.g. user-creation failures) are silently dropped
            // by requestErrorParser, so the user is stuck on the confirm step
            // with no message. Surface a generic recoverable notification.
            console.error('Registration failed', e);
            dispatch(
              addNotification({
                type: 'error',
                title:
                  "We couldn't complete your registration. Please try again in a moment, or contact support if the issue persists.",
              })
            );
          })
          .finally(() => setIsLoading(false));
      }

      case LoginStep.forgotPassword: {
        setIsLoading(true);
        return authService
          .sendVerificationCode(state.email, VerificationCodeType.RESET_PASSWORD)
          .then(() => {
            redirectUserTo(history, `/${LoginStep.restore}`);
          })
          .finally(() => setIsLoading(false));
      }

      case LoginStep.restore: {
        setIsLoading(true);
        return authService
          .changePasswordByToken(history, state.code, state.email, state.newPassword)
          .finally(() => setIsLoading(false));
      }

      default:
    }
  };

  const resetState = (callback?: () => void) => {
    setEmailVerification(null);
    setState({
      email: '',
      password: '',
      fullName: '',
      code: '',
      captcha: '',
      newPassword: '',
      showCaptcha: showCaptchaStatus?.show,
      type: AuthHandlerType.NONE,
    });

    if (callback) callback();
  };

  useEffect(() => {
    if (ref && ref.length > 0) {
      setReferralCode(ref as string);
    }
  }, [ref]);

  useEffect(() => {
    if (ps_xid && ps_xid.length > 0) {
      setAffiliateCode(ps_xid as string);
    }
  }, [ps_xid]);

  // Drop the one-time code from the URL once handled, so a refresh doesn't
  // re-exchange a spent code and the code doesn't linger in browser history.
  const stripAppSumoCodeFromUrl = () => {
    const { code, ...rest } = qs.parse(location.search);
    history.replace({ pathname: location.pathname, search: qs.stringify(rest) });
  };

  useEffect(() => {
    // Restore a previously handled AppSumo flow (page refresh mid-signup, or return
    // from a failed attempt) from the session cache instead of re-exchanging.
    let cached: { code?: string; accessToken?: string; refreshToken?: string; failed?: boolean } =
      null;
    try {
      const cachedRaw = sessionStorage.getItem(APPSUMO_SESSION_KEY);
      cached = cachedRaw ? JSON.parse(cachedRaw) : null;
    } catch {
      // Corrupt cache entry: treat as absent and fall through to a fresh exchange.
      cached = null;
    }

    if (cached?.code && (!appSumoCodeParam || cached.code === appSumoCodeParam)) {
      setAppSumoCode(cached.code);
      if (!cached.failed && cached.accessToken) {
        setAppSumoTokens({
          accessToken: cached.accessToken,
          refreshToken: cached.refreshToken,
        } as AppSumoTokensResponseType);
      }
      if (appSumoCodeParam) {
        stripAppSumoCodeFromUrl();
      }
      return;
    }

    if (appSumoCodeParam && appSumoCodeParam.length > 0) {
      setAppSumoCode(appSumoCodeParam as string);
      setIsLoading(true);
      getAppSumoAccessTokenApi(appSumoCodeParam as string)
        .then((tokens) => {
          setAppSumoTokens(tokens);
          sessionStorage.setItem(
            APPSUMO_SESSION_KEY,
            JSON.stringify({
              code: appSumoCodeParam,
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
            })
          );
          dispatch(
            addNotification({
              type: 'success',
              title: 'AppSumo code verified.',
            })
          );
        })
        .catch((e) => {
          console.error(e);
          // Fail closed: signup stays blocked (see guardAppSumoSignup) until the
          // buyer restarts from their AppSumo activation link, instead of silently
          // continuing as a TRIAL signup with no linked license.
          sessionStorage.setItem(
            APPSUMO_SESSION_KEY,
            JSON.stringify({ code: appSumoCodeParam, failed: true })
          );
          dispatch(
            addNotification({
              type: 'error',
              title:
                "We couldn't verify your AppSumo code. Please return to AppSumo and open your activation link again.",
            })
          );
        })
        .finally(() => {
          setIsLoading(false);
          stripAppSumoCodeFromUrl();
        });
    }
  }, [appSumoCodeParam]);

  useEffect(() => {
    setStep(location.pathname.slice(1));
  }, [location.pathname]);

  useEffect(() => {
    const isLoggedIn = !!getItemFromStorage('token');
    if (isLoggedIn && location.pathname === `/${LoginStep.trial}`) {
      return;
    }

    if (!!token && [`/${LoginStep.signUp}`, `/${LoginStep.auth}`].includes(location.pathname)) {
      return;
    }

    if (
      !state.email &&
      !loginResponse?.sessionToken &&
      location.pathname !== `/${LoginStep.welcome}`
    ) {
      redirectUserTo(history, `/${LoginStep.welcome}`);
    }
  }, [state.email, location.pathname, token]);

  useEffect(() => {
    if (location.pathname === `/${LoginStep.welcome}`) {
      resetState();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!!token && [`/${LoginStep.auth}`].includes(location.pathname)) {
      authService
        .loginWithToken(history, token as string, AuthHandlerType.MICROSOFT, getRealm())
        .catch(() => {
          redirectUserTo(history, `/${LoginStep.welcome}`);
        });
    }
  }, [location.pathname, token]);

  // Once the demo call is booked, continue to the referral-source questionary.
  useCalendlyEventListener({
    onEventScheduled: () => {
      if (step === LoginStep.onboardingConciergeScheduleCall) {
        redirectUserTo(history, `/${LoginStep.onboardingConciergeFirstOrder}`);
      }
    },
  });

  if (step === LoginStep.auth) {
    return <BasePreloaderScreen location={location} />;
  }

  let boxClass = 'app';
  let ImageComponent = LoginImageBig;

  if (isConciergeRealm()) {
    boxClass = 'concierge';
    ImageComponent = ConciergeLoginImageBig;
  } else if (isPublisherRealm()) {
    boxClass = 'publisher';
    ImageComponent = PublisherLoginImageBig;
  }

  let logoClassName = 'login-container__logo';

  if (
    step === LoginStep.onboardingConciergeFirstOrder ||
    step === LoginStep.onboardingConciergeScheduleCall
  ) {
    logoClassName += ' login-container__logo--small';
  }

  const isCalendlyShown = step === LoginStep.onboardingConciergeScheduleCall;

  return (
    <div className="login-page__container">
      <div className="login-modal">
        <div className="login-container__left">
          <div
            className={cn('login-container', {
              'login-container--narrow': step === LoginStep.trial,
            })}
          >
            {step !== LoginStep.trial && (
              <div className="login-container__logo-wrapper">
                <img
                  alt="Respona"
                  onClick={() => window.open('https://respona.com', '_self')}
                  src={responaLogo}
                  className={logoClassName}
                />

                <SVGIcon icon="purpleStar" />
              </div>
            )}

            {step === LoginStep.welcome && (
              <LoginSignupFlow
                state={state}
                isLoading={isLoading}
                handleInput={(...data) => {
                  setEmailVerification(null);
                  handleInput(...data);
                }}
                onSubmit={(s, r) => {
                  let requestedStep = LoginStep.welcome;
                  if (s != null && Object.values(LoginStep).includes(s)) {
                    requestedStep = s;
                  }
                  completeStep(requestedStep, r);
                }}
                setIsLoading={setIsLoading}
                verification={emailVerification}
                setLoginResponse={setLoginResponse}
                history={history}
                redirectUserTo={redirectUserTo}
                appSumoTokens={appSumoTokens}
                appSumoActive={appSumoFlowActive}
                appSumoBlocked={appSumoBlocked}
              />
            )}

            {step === LoginStep.login && (
              <LoginFlow
                state={state}
                isLoading={isLoading}
                handleInput={handleInput}
                onSubmit={() => completeStep(LoginStep.login)}
                history={history}
                redirectUserTo={redirectUserTo}
              />
            )}

            {step === LoginStep.signUp && (
              <SignUpFlow
                state={state}
                isLoading={isLoading}
                handleInput={handleInput}
                onSubmit={() => completeStep(LoginStep.signUp)}
                history={history}
                redirectUserTo={redirectUserTo}
              />
            )}

            {step === LoginStep.confirm && (
              <CodeConfirmationFlow
                state={state}
                isLoading={isLoading}
                handleInput={handleInput}
                onSubmit={() => completeStep(LoginStep.confirm)}
                history={history}
                redirectUserTo={redirectUserTo}
              />
            )}

            {step === LoginStep.onboardingConciergeFirstOrder && (
              <ConciergeOnboardingTransitionFlow
                state={state}
                isLoading={isLoading}
                handleInput={handleInput}
                onSubmit={() => completeStep(LoginStep.confirm)}
                history={history}
                redirectUserTo={redirectUserTo}
              />
            )}

            {step === LoginStep.onboardingConciergeScheduleCall && (
              <ConciergeOnboardingScheduleCall
                state={state}
                isLoading={isLoading}
                handleInput={handleInput}
                onSubmit={() => completeStep(LoginStep.confirm)}
                history={history}
                redirectUserTo={redirectUserTo}
                userEmail={loginResponse?.userEmail || state.email}
              />
            )}

            {step === LoginStep.onboardingApp && (
              <OnboardingSessionFlow
                state={state}
                isLoading={isLoading}
                updateRef={updateRef}
                onSubmit={() => completeStep(LoginStep.onboardingApp)}
                history={history}
                redirectUserTo={redirectUserTo}
              />
            )}

            {step === LoginStep.forgotPassword && (
              <RequestUpdatePasswordFlow
                state={state}
                isLoading={isLoading}
                handleInput={handleInput}
                onSubmit={() => completeStep(LoginStep.forgotPassword)}
                history={history}
                redirectUserTo={redirectUserTo}
              />
            )}
            {step === LoginStep.restore && (
              <UpdatePasswordFlow
                state={state}
                isLoading={isLoading}
                handleInput={handleInput}
                onSubmit={() => completeStep(LoginStep.restore)}
                history={history}
                redirectUserTo={redirectUserTo}
              />
            )}

            {step === LoginStep.trial && (
              <>
                <div
                  className="login-container__logout-button"
                  style={{ backgroundColor: '#fff' }}
                  onClick={() => {
                    setIsLoading(true);
                    authService.logout(history).finally(() => {
                      setIsLoading(false);
                    });
                  }}
                >
                  <QuestionTooltip
                    text={isLoading ? 'Logging out...' : 'Log out'}
                    style={{ width: '100%', height: '100%' }}
                    icon={isLoading ? null : 'logout'}
                    color="#221CB6"
                  >
                    <Loader isLoading style={{ width: '16px', height: '16px' }} />
                  </QuestionTooltip>
                </div>
                <LoginPagePaymentFlow />
              </>
            )}
          </div>

          {isSuccess && state.showCaptcha ? (
            <div className="register-page-content__recaptcha-wrapper" data-testid="login-recaptcha">
              <ReCAPTCHA
                sitekey="6LdTIMQUAAAAAEAEI73rFGj7DT0T2MpafqvaEpY4"
                onChange={(value) => handleInput('captcha', value)}
              />
            </div>
          ) : null}
        </div>
        <div
          className={`login-container__right ${isRightSideVisible ? 'login-container__right--mobile-visible' : ''}`}
        >
          <div
            className={`login-container__right__box ${boxClass} ${isCalendlyShown ? 'calendly-block' : ''}`}
          >
            {isCalendlyShown ? (
              <>
                <span
                  className={cn('modal__cross-btn')}
                  onClick={() => redirectUserTo(history, location.pathname)}
                  tabIndex={0}
                  role="button"
                >
                  <SVGIcon icon="crossBlack" />
                </span>

                <InlineWidget
                  url="https://calendly.com/respona/onboarding"
                  iframeTitle="onboarding-scheduling"
                  pageSettings={{
                    hideLandingPageDetails: true,
                    hideGdprBanner: true,
                    textColor: '111a45',
                    primaryColor: '251fb6',
                  }}
                  styles={{
                    width: '80%',
                    minWidth: '220px',
                    maxWidth: '480px',
                    maxHeight: '700px',
                    height: '90%',
                    borderRadius: '20px',
                  }}
                  prefill={{
                    name: state.fullName,
                    email: loginResponse?.userEmail || state.email,
                    guests: [],
                  }}
                />
              </>
            ) : (
              <img
                src={ImageComponent}
                className="login-container__right__login-image-big"
                alt=""
              />
            )}
          </div>
        </div>
      </div>
      {/* </div> */}
    </div>
  );
}

export default LoginPage;
